import { action, makeObservable, observable } from 'mobx';
import moment from 'moment';
import { localeStore } from '~/mobx/localesStore';
import { IPickerOption } from '~/view/components/Controls/Pickers/Picker/types';
import client from '~/services/client';
import { toastSuccess } from '~/utils/toasts';
import logger from '~/services/logger';
import { PostingTime } from '~/view/screens/MarketingScreen/screens/MessageCampaign/types';
import { dateFormatForRequest, defaultTime, fullWeek } from '~/constants/dateTime';
import { TCampaignFieldErrors, TMessageCampaignForm } from '~/view/screens/MarketingScreen/screens/MessageCampaign/CampaignEditScreen/types';
import { castCampaignData, isNightTime } from '~/view/screens/MarketingScreen/screens/MessageCampaign/CampaignEditScreen/helpers';
import { DELIVERY_METHOD } from '~/constants/deliveryTypes';
import { defaultValidation } from '~/constants/main';

const defaultForm: TMessageCampaignForm = {
  subject: '',
  message: '',
  startAt: new Date(),
  endAt: new Date(),
  language: localeStore.language,
  postingType: PostingTime.NOW,
  daysOfWeek: fullWeek,
  settings: new Map(fullWeek.map((day) => [day, { weekday: day, startTime: defaultTime, isValid: true }])),
};

const defaultDeliveryMethod: IPickerOption = {
  label: localeStore.t(DELIVERY_METHOD.APP_NOTIFICATION),
  value: 1,
};

export class MessageCampaignStore {
  constructor() {
    makeObservable(this);
  }

  @observable public loading = false;
  @observable public deliveryMethod: IPickerOption = defaultDeliveryMethod;
  @observable public form: TMessageCampaignForm = defaultForm;
  @observable public validationErrors: TCampaignFieldErrors = { time: defaultValidation, subject: defaultValidation, message: defaultValidation };
  @observable public isEdited = false;

  @action public changeLoading = (loading: boolean) => {
    this.loading = loading;
  };

  @action public changeEdited = (isEdited: boolean) => {
    this.isEdited = isEdited;
  };

  @action public changeDeliveryMethod = (deliveryMethod: IPickerOption) => {
    this.deliveryMethod = deliveryMethod;
    this.changeEdited(true);
  };

  @action public changeForm = (data: Partial<any>): void => {
    this.form = { ...this.form, ...data };
    this.changeEdited(true);
  };

  @action fetchCampaignSettings = async (id: string) => {
    try {
      this.changeLoading(true);

      const response = await client.get(`v3/message_campaigns/${id}`);

      this.form = castCampaignData(response.data.message_campaign);
    } catch (error: any) {
      logger.error({ error, place: 'MessageCampaignArea fetchCampaignSettings', showModal: true });
    } finally {
      this.changeLoading(false);
    }
  };

  @action createCampaign = async () => {
    try {
      this.changeLoading(true);

      await client.post(`v3/message_campaigns`, this.getDataForSaving(), { timeout: 60000 });

      this.resetForm();

      toastSuccess({ message: localeStore.t('toast_message_campaign_sent') });
    } catch (error: any) {
      logger.error({ error, place: 'MessageCampaignArea clickSend', showModal: true });
    } finally {
      this.changeLoading(false);
    }
  };

  @action updateCampaign = async () => {
    try {
      this.changeLoading(true);

      await client.put(`v3/message_campaigns/${this.form.id}`, this.getDataForSaving(), { timeout: 60000 });

      this.isEdited = false;

      toastSuccess({ message: localeStore.t('toast_message_campaign_sent') });
    } catch (error: any) {
      logger.error({ error, place: 'MessageCampaignArea clickSend', showModal: true });
    } finally {
      this.changeLoading(false);
    }
  };

  @action deleteCampaign = async (id: number) => {
    try {
      this.changeLoading(true);
      await client.delete(`v3/message_campaigns/${id}`);
      this.resetForm();
    } catch (error) {
      logger.error({ error, place: 'MessageCampaignsList deleteCampaign', showModal: true });
    } finally {
      this.changeLoading(false);
    }
  };

  @action validateForm = () => {
    this.validateSubject();
    this.validateMessage();
    this.validateTime();
  };

  @action validateSubject = () => {
    this.validationErrors.subject.isValid = this.form.subject.trim().length !== 0;
  };

  @action validateMessage = () => {
    this.validationErrors.message.isValid = this.form.message.trim().length !== 0;
  };

  @action validateTime = () => {
    if (
      (this.form.postingType === PostingTime.NOW && isNightTime(new Date())) ||
      (this.form.postingType === PostingTime.LATER && isNightTime(this.form.startAt))
    ) {
      this.validationErrors.time = { errorText: localeStore.t('marketing_mc_restricted_range_err'), isValid: false };
    } else {
      this.validationErrors.time.isValid = true;
    }
  };

  @action public resetForm = () => {
    this.form = defaultForm;
    this.deliveryMethod = defaultDeliveryMethod;
    this.isEdited = false;
    this.validationErrors = { time: defaultValidation, subject: defaultValidation, message: defaultValidation };
  };

  getDataForSaving = () => {
    const data: any = {
      delivery_method: this.deliveryMethod.value,
      title: this.form.subject,
      message: this.form.message,
      posting_type: this.form.postingType,
      start_at: moment(this.form.startAt).format(dateFormatForRequest),
      end_at: moment(this.form.endAt).format(dateFormatForRequest),
    };

    if (this.form.postingType === PostingTime.NOW) {
      data.start_at = moment().format(dateFormatForRequest);
      data.end_at = moment().format(dateFormatForRequest);
      data.settings = [{ weekday: moment().isoWeekday(), status: true, time: moment().utc().format('HH:mm:ss') }];
    }

    if (this.form.postingType === PostingTime.LATER) {
      data.settings = [{ weekday: moment(this.form.startAt).isoWeekday(), status: true, time: moment(this.form.startAt).utc().format('HH:mm:ss') }];
    }

    if (this.form.postingType === PostingTime.SCHEDULED) {
      data.settings = Array.from(this.form.settings, ([weekday, setting]) => ({
        id: setting.id,
        weekday,
        status: this.form.daysOfWeek.includes(weekday),
        time: moment(setting.startTime).utc().format('HH:mm:ss'),
      }));
    }

    return data;
  };
}

export const messageCampaignStore = new MessageCampaignStore();
